<!--
// app/components/pages/region-world-cup/BracketTablesWorldCup.vue
-->
<script setup lang="ts">
import type { BracketData } from "~/src/world-cup/domain/models/Bracket";
import type { MatchCenterData } from "~/src/match/domain/models/Match";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { arrowsSliderVisibility } from "~/src/Shared/utils";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import BracketWorldCupDatasource2024 from "~/src/world-cup/datasources/WorldCupBracket.json";
import SliderArrowsBehaviour from "~/app/components/Shared/slider-matches/SliderArrowsBehaviour.vue";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import NewBracketWorldCup from "~/app/components/pages/region-world-cup/NewBracketWorldCup.vue";
import BracketWorldCupFirstEdition from "~/app/components/pages/region-world-cup/BracketWorldCupFirstEdition.vue";
import { useSeasons } from "~/app/composables/season/seasons";
import { teamArrToObjById } from "~/src/team/domain/services/teamService";

type Props = {
  isHomeComponent?: boolean;
  inOtherLeaguesHome?: boolean;
  seasonIdData?: number;
};

const props = defineProps<Props>();

const { currentSeasonTeams, currentSeason } = useCompetitionStore();
const { getSeasonTeams, useFetchParamsSeasonMatchCenterData } = useSeasons();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { locale } = useI18n();

const teamsObj = ref({});
const bracketData = ref<BracketData | MatchCenterData[]>();
const bracketKey = ref(0);

const getBracketData = async () => {
  const _seasonId = props.seasonIdData ? props.seasonIdData : seasonId.value;

  await nextTick();

  if (currentSeason.id === _seasonId) {
    teamsObj.value = await teamArrToObjById(currentSeasonTeams, currentSeason.id);
    bracketData.value = teamsObj.value;
  } else {
    const teams = await getSeasonTeams(_seasonId);
    teamsObj.value = await teamArrToObjById(teams, _seasonId);
  }

  if (_seasonId === 13) {
    bracketData.value = BracketWorldCupDatasource2024;
  } else {
    const { path, opts } = useFetchParamsSeasonMatchCenterData(_seasonId, locale.value);
    const { data, error } = await useFetch(path, opts);

    if (error.value) {
      console.error(error.value);
      bracketData.value = [];
    }

    bracketData.value = data.value ? data.value : [];
  }
  bracketKey.value++;
  domModificationsSlider();
};

await getBracketData();

function domModificationsSlider() {
  const tablesContainer: HTMLElement | null = document.getElementById("tablesSlider");
  const bracketButtonLeft: HTMLElement | null = document.getElementById("bracketButtonLeft");
  const bracketButtonRight: HTMLElement | null = document.getElementById("bracketButtonRight");

  if (tablesContainer) {
    let childrenTablesContainerWidth: number = 0;
    const childrenTablesContainer = [...tablesContainer.children];
    childrenTablesContainer.forEach(item => (childrenTablesContainerWidth += item.clientWidth));
    const swingWrapper = tablesContainer.querySelector(".swing");

    const stagesBracket: NodeListOf<HTMLElement> = tablesContainer.querySelectorAll(
      ".round-col-container.active-stage"
    );
    const lastStagePlayed: HTMLElement | null = stagesBracket[stagesBracket.length - 1];
    tablesContainer.scrollLeft = 0;

    if (lastStagePlayed && tablesContainer) {
      tablesContainer.scrollLeft = lastStagePlayed.getBoundingClientRect().x - 80;
    }

    if (swingWrapper) {
      if (
        Math.round(tablesContainer.scrollLeft) ===
        Math.round(tablesContainer.scrollWidth) - Math.round(tablesContainer.clientWidth)
      )
        //if is totally right
        swingWrapper.classList.remove("swing");
    }

    tablesContainer.addEventListener("scroll", () => {
      if (swingWrapper) {
        swingWrapper.classList.remove("swing");
      }
      arrowsSliderVisibility("general", tablesContainer, bracketButtonLeft, bracketButtonRight);
    });
  }
}

onMounted(() => {
  domModificationsSlider();
});

onUpdated(() => {
  domModificationsSlider();
});
</script>

<template>
  <div v-if="!isHomeComponent" class="text-right">
    <seasons-filter
      v-model:season-id="seasonId"
      v-model:season-cluster-id="seasonClusterId"
      @change="getBracketData"
    />
  </div>

  <slider-arrows-behaviour
    button-right-id="bracketButtonRight"
    button-left-id="bracketButtonLeft"
    :button-scroll-distance="204"
    slider-container-id="tablesSlider"
    :conditionally-render-if="!!bracketData"
  >
    <bracket-world-cup-first-edition
      v-if="seasonId === 13 && teamsObj && bracketData"
      :teams-obj="teamsObj"
      :bracket-data="bracketData"
      :bracket-key="bracketKey"
    ></bracket-world-cup-first-edition>
    <new-bracket-world-cup
      v-else
      :is-home-component="isHomeComponent"
      :season-id-data="seasonIdData ? seasonIdData : seasonId"
      :in-other-leagues-home="inOtherLeaguesHome"
      :teams-obj="teamsObj"
      :bracket-data="bracketData"
      :bracket-key="bracketKey"
    ></new-bracket-world-cup>
  </slider-arrows-behaviour>
</template>

<style scoped></style>
